/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * PlatformOps Widget Definitions
 *
 * Defines all widgets for the PlatformOps/CloudOps dashboard using the template system.
 * Each widget specifies its data transformation from the source data.
 */

import {
  createMetricWidget,
  createChartWidget,
  WIDGET_CATEGORIES,
  WIDGET_SIZES,
  WIDGET_ICONS,
  DATA_SOURCES,
  WidgetTemplate,
} from '../widgetTemplates';
import { GREEN, RED, AMBER, YELLOW, BLUE, CYAN } from '../../../constants/chartColors';

const {
  FiActivity,
  FiCloud,
  FiCheckCircle,
  FiBarChart2,
  FiServer,
  FiAlertTriangle,
  FiZap,
  FiTag,
  FiTrendingUp,
  FiTool,
  FiArchive,
  FiDollarSign,
  FiUsers,
  FiLock,
  FiShield,
  FiXCircle,
  FiCrop,
  FiMoon,
  FiBell,
} = WIDGET_ICONS;

/**
 * Platform Health Score Widget
 * Shows overall operational status
 */
export const platformHealthWidget = createMetricWidget({
  id: 'platform-health',
  name: 'Platform Health Score',
  description: 'Overall operational status',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 60000,

  getData: (sourceData) => ({
    value: sourceData?.platformHealth || 0,
    label: 'Platform Health Score',
    format: 'percentage',
    color: 'aqua',
    link: '/cloudops',
  }),
});

/**
 * Total Cloud Spend Widget
 * Shows current month cloud expenditure
 */
export const totalCloudSpendWidget = createMetricWidget({
  id: 'total-cloud-spend',
  name: 'Total Cloud Spend',
  description: 'Current month cloud expenditure',
  icon: FiCloud,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: true,
  refreshRate: 300000,

  getData: (sourceData) => ({
    value: sourceData?.totalCloudSpend || 0,
    label: 'Total Cloud Spend',
    format: 'currency',
    color: 'blue',
    link: '/cloudops/finops',
  }),
});

/**
 * Compliance Score Widget
 * Shows overall compliance rate
 */
export const complianceScoreWidget = createMetricWidget({
  id: 'compliance-score',
  name: 'Compliance Score',
  description: 'Overall compliance rate',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,

  getData: (sourceData) => ({
    value: sourceData?.complianceScore || 0,
    label: 'Compliance Score',
    format: 'percentage',
    color: 'aqua',
    link: '/cloudops/guardrails',
  }),
});

/**
 * Cost by Service Widget
 * Shows service-level cost breakdown as a pie chart
 */
export const costByServiceWidget = createChartWidget({
  id: 'cost-by-service',
  name: 'Cost by AWS Service',
  description: 'Service-level cost breakdown',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: true,
  refreshRate: 300000,

  getData: (sourceData) => {
    const serviceBreakdown = sourceData?.serviceBreakdown || [];

    const data = Array.isArray(serviceBreakdown)
      ? serviceBreakdown.map((item: Record<string, unknown>) => ({
          name: (item.service as string) || 'Unknown',
          value: Number.parseFloat(String(item.cost || 0)),
        }))
      : [];

    return {
      chartType: 'pie',
      data,
      dataKey: 'value',
      link: '/cloudops/finops',
    };
  },
});

/**
 * Active Environments Widget
 * Shows number of active endpoints
 */
export const activeEnvironmentsWidget = createMetricWidget({
  id: 'active-environments',
  name: 'Active Endpoints',
  description: 'Number of active endpoints',
  icon: FiServer,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 60000,

  getData: (sourceData) => ({
    value: sourceData?.activeEnvironments || 0,
    label: 'Active Endpoints',
    format: 'number',
    color: 'blue',
    link: '/cloudops/endpoints',
  }),
});

/**
 * Open Violations Widget
 * Shows active policy violations
 */
export const openViolationsWidget = createMetricWidget({
  id: 'open-violations',
  name: 'Open Violations',
  description: 'Active policy violations',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 60000,

  getData: (sourceData) => ({
    value: sourceData?.openViolations || 0,
    label: 'Open Policy Violations',
    format: 'number',
    color: 'darkcyan',
    link: '/cloudops/guardrails',
  }),
});

/**
 * Estimated Savings Widget
 * Shows potential cost reduction
 */
export const estimatedSavingsWidget = createMetricWidget({
  id: 'estimated-savings',
  name: 'Estimated Savings',
  description: 'Potential cost reduction',
  icon: FiZap,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,

  getData: (sourceData) => ({
    value: sourceData?.estimatedSavings || 0,
    label: 'Potential Savings',
    format: 'currency',
    color: 'aqua',
    link: '/cloudops/finops',
  }),
});

// ============================================
// Additional PlatformOps Widgets (Phase 10)
// ============================================

/**
 * Environment Health Widget
 * Shows health status by endpoint
 */
export const environmentHealthWidget = createChartWidget({
  id: 'environment-health',
  name: 'Endpoint Health',
  description: 'Health status by endpoint',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const envHealth = sourceData?.environmentHealth || sourceData?.env_status || [];
    return {
      chartType: 'bar',
      data: envHealth,
      xKey: 'env',
      yKey: 'health',
      color: CYAN,
      link: '/cloudops/endpoints',
    };
  },
});

/**
 * Tag Compliance Widget
 * Shows resource tagging compliance rate
 */
export const tagComplianceWidget = createMetricWidget({
  id: 'tag-compliance',
  name: 'Tag Compliance',
  description: 'Resource tagging compliance rate',
  icon: FiTag,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.tagCompliance || sourceData?.tag_compliance_rate || 0,
    label: 'Tag Compliance',
    format: 'percentage',
    color: 'aqua',
    link: '/cloudops/tagging',
  }),
});

/**
 * Cost Anomalies Widget
 * Shows unusual spending patterns detected
 */
export const costAnomaliesWidget = createMetricWidget({
  id: 'cost-anomalies',
  name: 'Cost Anomalies',
  description: 'Unusual spending patterns detected',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.costAnomalies || sourceData?.anomaly_count || 0,
    label: 'Cost Anomalies',
    format: 'number',
    color: 'amber',
    link: '/cloudops/finops',
  }),
});

/**
 * Monthly Cost Trend Widget
 * Shows historical spending pattern
 */
export const monthlyCostTrendWidget = createChartWidget({
  id: 'monthly-cost-trend',
  name: 'Monthly Cost Trend',
  description: 'Historical spending pattern',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.LARGE,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const trend = sourceData?.monthlyCostTrend || sourceData?.cost_history || [];
    return {
      chartType: 'area',
      data: trend,
      xKey: 'month',
      yKey: 'cost',
      color: CYAN,
      link: '/cloudops/finops',
    };
  },
});

/**
 * Service Cost Breakdown Alt Widget
 * Shows service-level cost breakdown
 */
export const serviceCostBreakdownAltWidget = createChartWidget({
  id: 'service-cost-breakdown-alt',
  name: 'Service Cost Breakdown',
  description: 'Service-level cost breakdown',
  icon: FiTool,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const breakdown = sourceData?.serviceCostBreakdown || sourceData?.serviceBreakdown || [];
    const data = breakdown.map((item: Record<string, unknown>) => ({
      name: (item.service || item.name) as string,
      value: Number.parseFloat(String(item.cost || item.value || 0)),
    }));
    return {
      chartType: 'horizontalBar',
      data,
      xKey: 'name',
      yKey: 'value',
      color: CYAN,
      link: '/cloudops/finops',
    };
  },
});

/**
 * RI Utilization Widget
 * Shows Reserved Instance usage
 */
export const riUtilizationWidget = createMetricWidget({
  id: 'ri-utilization',
  name: 'RI Utilization',
  description: 'Reserved Instance usage',
  icon: FiArchive,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.riUtilization || sourceData?.ri_utilization_rate || 0,
    label: 'RI Utilization',
    format: 'percentage',
    color: 'green',
    link: '/cloudops/finops',
  }),
});

/**
 * Spot Savings Widget
 * Shows cost reduction from spot instances
 */
export const spotSavingsWidget = createMetricWidget({
  id: 'spot-savings',
  name: 'Spot Savings',
  description: 'Cost reduction from spot',
  icon: FiDollarSign,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.spotSavings || sourceData?.spot_savings || 0,
    label: 'Spot Savings',
    format: 'currency',
    color: 'green',
    link: '/cloudops/finops',
  }),
});

/**
 * Cost Savings Opportunities Widget
 * Shows potential cost savings
 */
export const costSavingsOpportunitiesWidget = createMetricWidget({
  id: 'cost-savings-opportunities',
  name: 'Savings Opportunities',
  description: 'Potential cost savings',
  icon: FiZap,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.savingsOpportunities || sourceData?.potential_savings || 0,
    label: 'Potential Savings',
    format: 'currency',
    color: 'aqua',
    link: '/cloudops/finops',
  }),
});

/**
 * Violations by Severity Widget
 * Shows Critical/High/Medium/Low breakdown
 */
export const violationsBySeverityWidget = createChartWidget({
  id: 'violations-by-severity',
  name: 'Violations by Severity',
  description: 'Critical/High/Medium/Low breakdown',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => {
    const severity = sourceData?.violationsBySeverity || sourceData?.severity_breakdown || [];
    // Transform to use 'name' key for ChartWidget compatibility
    const data = severity.map((item: Record<string, unknown>) => ({
      name: (item.severity || item.name) as string,
      value: (item.count || item.value || 0) as number,
    }));
    return {
      chartType: 'donut',
      data,
      dataKey: 'value',
      colors: [RED, AMBER, YELLOW, GREEN],
      link: '/cloudops/guardrails',
    };
  },
});

/**
 * Violations by Category Widget
 * Shows Security/Cost/Governance breakdown
 */
export const violationsByCategoryWidget = createChartWidget({
  id: 'violations-by-category',
  name: 'Violations by Category',
  description: 'Security/Cost/Governance breakdown',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => {
    const categories = sourceData?.violationsByCategory || sourceData?.category_breakdown || [];
    return {
      chartType: 'bar',
      data: categories,
      xKey: 'category',
      yKey: 'count',
      color: CYAN,
      link: '/cloudops/guardrails',
    };
  },
});

/**
 * Endpoint Uptime Widget
 * Shows average uptime percentage
 */
export const environmentUptimeWidget = createMetricWidget({
  id: 'environment-uptime',
  name: 'Endpoint Uptime',
  description: 'Average uptime percentage',
  icon: FiZap,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.environmentUptime || sourceData?.avg_uptime || 0,
    label: 'Uptime',
    format: 'percentage',
    color: 'green',
    link: '/cloudops/endpoints',
  }),
});

/**
 * Active Users Count Widget
 * Shows total active user accounts
 */
export const activeUsersCountWidget = createMetricWidget({
  id: 'active-users-count',
  name: 'Active Users',
  description: 'Total active user accounts',
  icon: FiUsers,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.activeUsers || sourceData?.active_user_count || 0,
    label: 'Active Users',
    format: 'number',
    color: 'blue',
    link: '/cloudops/access',
  }),
});

/**
 * Pending Access Requests Widget
 * Shows awaiting approval count
 */
export const pendingAccessRequestsWidget = createMetricWidget({
  id: 'pending-access-requests',
  name: 'Pending Access Requests',
  description: 'Awaiting approval',
  icon: FiLock,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.pendingAccessRequests || sourceData?.pending_requests || 0,
    label: 'Pending Requests',
    format: 'number',
    color: 'amber',
    link: '/cloudops/access',
  }),
});

/**
 * Burn Rate vs Planned Widget
 * Shows actual vs planned spending
 */
export const burnRateVsPlannedWidget = createChartWidget({
  id: 'burn-rate-vs-planned',
  name: 'Burn Rate vs Planned',
  description: 'Actual vs planned spending',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.LARGE,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const burnData = sourceData?.burnRateVsPlanned || sourceData?.budget_comparison || [];
    return {
      chartType: 'line',
      data: burnData,
      xKey: 'date',
      yKeys: ['actual', 'planned'],
      colors: [CYAN, BLUE],
      labels: ['Actual', 'Planned'],
      link: '/cloudops/finops',
    };
  },
});

/**
 * Enforcement Mode Widget
 * Shows Prevent vs Alert rules
 */
export const enforcementModeWidget = createChartWidget({
  id: 'enforcement-mode',
  name: 'Enforcement Mode',
  description: 'Prevent vs Alert rules',
  icon: FiShield,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => {
    const modes = sourceData?.enforcementModes || sourceData?.rule_modes || [];
    // Transform to use 'name' key for ChartWidget compatibility
    const data = modes.map((item: Record<string, unknown>) => ({
      name: (item.mode || item.name) as string,
      value: (item.count || item.value || 0) as number,
    }));
    return {
      chartType: 'pie',
      data,
      dataKey: 'value',
      colors: [RED, AMBER],
      link: '/cloudops/guardrails',
    };
  },
});

/**
 * Compliance Trend Widget
 * Shows historical compliance rate
 */
export const complianceTrendWidget = createChartWidget({
  id: 'compliance-trend',
  name: 'Compliance Trend',
  description: 'Historical compliance rate',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.LARGE,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const trend = sourceData?.complianceTrend || sourceData?.compliance_history || [];
    return {
      chartType: 'area',
      data: trend,
      xKey: 'week',
      yKey: 'rate',
      color: GREEN,
      link: '/cloudops/guardrails',
    };
  },
});

/**
 * Total Users Widget
 * Shows all users in system
 */
export const totalUsersWidget = createMetricWidget({
  id: 'total-users',
  name: 'Total Users',
  description: 'All users in system',
  icon: FiUsers,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.totalUsers || sourceData?.user_count || 0,
    label: 'Total Users',
    format: 'number',
    color: 'blue',
    link: '/cloudops/access',
  }),
});

/**
 * Roles Configured Widget
 * Shows number of unique roles
 */
export const rolesConfiguredWidget = createMetricWidget({
  id: 'roles-configured',
  name: 'Roles Configured',
  description: 'Number of unique roles',
  icon: FiLock,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.rolesConfigured || sourceData?.role_count || 0,
    label: 'Roles',
    format: 'number',
    color: 'aqua',
    link: '/cloudops/access',
  }),
});

/**
 * User Activity Rate Widget
 * Shows Active/Total ratio
 */
export const userActivityRateWidget = createMetricWidget({
  id: 'user-activity-rate',
  name: 'User Activity Rate',
  description: 'Active/Total ratio',
  icon: FiZap,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => {
    const active = sourceData?.activeUsers || 0;
    const total = sourceData?.totalUsers || 1;
    const rate = sourceData?.userActivityRate || (total > 0 ? (active / total) * 100 : 0);
    return {
      value: Math.round(rate * 10) / 10,
      label: 'Activity Rate',
      format: 'percentage',
      color: 'green',
      link: '/cloudops/access',
    };
  },
});

/**
 * Access by Role Widget
 * Shows users per role
 */
export const accessByRoleWidget = createChartWidget({
  id: 'access-by-role',
  name: 'Access by Role',
  description: 'Users per role',
  icon: FiUsers,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => {
    const roles = sourceData?.accessByRole || sourceData?.role_distribution || [];
    return {
      chartType: 'horizontalBar',
      data: roles,
      xKey: 'role',
      yKey: 'users',
      color: CYAN,
      link: '/cloudops/access',
    };
  },
});

/**
 * Failed Logins Widget
 * Shows security monitoring
 */
export const failedLoginsWidget = createMetricWidget({
  id: 'failed-logins',
  name: 'Failed Logins',
  description: 'Security monitoring',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.failedLogins || sourceData?.failed_login_count || 0,
    label: 'Failed Logins',
    format: 'number',
    color: 'red',
    link: '/cloudops/access',
  }),
});

/**
 * Healthy Endpoints Widget
 * Shows count of healthy endpoints
 */
export const healthyEnvironmentsWidget = createMetricWidget({
  id: 'healthy-environments',
  name: 'Healthy Endpoints',
  description: 'Count of healthy endpoints',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.healthyEnvironments || sourceData?.healthy_env_count || 0,
    label: 'Healthy',
    format: 'number',
    color: 'green',
    link: '/cloudops/endpoints',
  }),
});

/**
 * Degraded Endpoints Widget
 * Shows endpoints with issues
 */
export const degradedEnvironmentsWidget = createMetricWidget({
  id: 'degraded-environments',
  name: 'Degraded Endpoints',
  description: 'Endpoints with issues',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.degradedEnvironments || sourceData?.degraded_env_count || 0,
    label: 'Degraded',
    format: 'number',
    color: 'amber',
    link: '/cloudops/endpoints',
  }),
});

/**
 * Down Endpoints Widget
 * Shows unavailable endpoints
 */
export const downEnvironmentsWidget = createMetricWidget({
  id: 'down-environments',
  name: 'Down Endpoints',
  description: 'Unavailable endpoints',
  icon: FiXCircle,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.downEnvironments || sourceData?.down_env_count || 0,
    label: 'Down',
    format: 'number',
    color: 'red',
    link: '/cloudops/endpoints',
  }),
});

/**
 * Tag Compliance Rate Widget
 * Shows overall tagging compliance
 */
export const tagComplianceRateWidget = createMetricWidget({
  id: 'tag-compliance-rate',
  name: 'Tag Compliance Rate',
  description: 'Overall tagging compliance',
  icon: FiTag,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.tagComplianceRate || sourceData?.tag_compliance || 0,
    label: 'Tag Compliance',
    format: 'percentage',
    color: 'aqua',
    link: '/cloudops/tagging',
  }),
});

/**
 * Tagged Resources Widget
 * Shows resources with tags
 */
export const taggedResourcesWidget = createMetricWidget({
  id: 'tagged-resources',
  name: 'Tagged Resources',
  description: 'Resources with tags',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.taggedResources || sourceData?.tagged_count || 0,
    label: 'Tagged',
    format: 'number',
    color: 'green',
    link: '/cloudops/tagging',
  }),
});

/**
 * Untagged Resources Widget
 * Shows resources missing tags
 */
export const untaggedResourcesWidget = createMetricWidget({
  id: 'untagged-resources',
  name: 'Untagged Resources',
  description: 'Resources missing tags',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.untaggedResources || sourceData?.untagged_count || 0,
    label: 'Untagged',
    format: 'number',
    color: 'amber',
    link: '/cloudops/tagging',
  }),
});

/**
 * Tag Coverage Widget
 * Shows coverage per tag type
 */
export const tagCoverageWidget = createChartWidget({
  id: 'tag-coverage',
  name: 'Tag Coverage',
  description: 'Coverage per tag type',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => {
    const coverage = sourceData?.tagCoverage || sourceData?.tag_type_coverage || [];
    return {
      chartType: 'bar',
      data: coverage,
      xKey: 'tag',
      yKey: 'coverage',
      color: CYAN,
      link: '/cloudops/tagging',
    };
  },
});

/**
 * Cost Allocation Rate Widget
 * Shows cost allocated via tags
 */
export const costAllocationRateWidget = createMetricWidget({
  id: 'cost-allocation-rate',
  name: 'Cost Allocation',
  description: 'Cost allocated via tags',
  icon: FiDollarSign,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.costAllocationRate || sourceData?.allocation_rate || 0,
    label: 'Allocation',
    format: 'percentage',
    color: 'blue',
    link: '/cloudops/tagging',
  }),
});

/**
 * Spending Anomalies Widget
 * Shows unusual spending patterns
 */
export const spendingAnomaliesWidget = createMetricWidget({
  id: 'spending-anomalies',
  name: 'Spending Anomalies',
  description: 'Unusual spending patterns',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.spendingAnomalies || sourceData?.anomalies_count || 0,
    label: 'Anomalies',
    format: 'number',
    color: 'red',
    link: '/cloudops/finops',
  }),
});

/**
 * Rightsizing Recommendations Widget
 * Shows instance optimization suggestions
 */
export const rightsizingRecommendationsWidget = createMetricWidget({
  id: 'rightsizing-recommendations',
  name: 'Rightsizing Recs',
  description: 'Instance optimization',
  icon: FiCrop,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.rightsizingRecommendations || sourceData?.rightsize_count || 0,
    label: 'Recommendations',
    format: 'number',
    color: 'aqua',
    link: '/cloudops/finops',
  }),
});

/**
 * Idle Resources Widget
 * Shows unused resource count
 */
export const idleResourcesWidget = createMetricWidget({
  id: 'idle-resources',
  name: 'Idle Resources',
  description: 'Unused resource count',
  icon: FiMoon,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.idleResources || sourceData?.idle_count || 0,
    label: 'Idle Resources',
    format: 'number',
    color: 'amber',
    link: '/cloudops/finops',
  }),
});

/**
 * System Uptime Widget
 * Shows platform availability
 */
export const systemUptimeWidget = createMetricWidget({
  id: 'system-uptime',
  name: 'System Uptime',
  description: 'Platform availability',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.systemUptime || sourceData?.uptime_percent || 0,
    label: 'Uptime',
    format: 'percentage',
    color: 'green',
    link: '/cloudops',
  }),
});

/**
 * Monthly Budget Widget
 * Shows budget allocation
 */
export const monthlyBudgetWidget = createMetricWidget({
  id: 'monthly-budget',
  name: 'Monthly Budget',
  description: 'Budget allocation',
  icon: FiDollarSign,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 86400000,
  getData: (sourceData) => ({
    value: sourceData?.monthlyBudget || sourceData?.budget_amount || 0,
    label: 'Monthly Budget',
    format: 'currency',
    color: 'blue',
    link: '/cloudops/finops',
  }),
});

/**
 * Budget Alerts Widget
 * Shows budget threshold alerts
 */
export const budgetAlertsWidget = createMetricWidget({
  id: 'budget-alerts',
  name: 'Budget Alerts',
  description: 'Budget threshold alerts',
  icon: FiBell,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.budgetAlerts || sourceData?.budget_alert_count || 0,
    label: 'Budget Alerts',
    format: 'number',
    color: 'amber',
    link: '/cloudops/finops',
  }),
});

/**
 * Alert Volume Widget
 * Shows alerts over time
 */
export const alertVolumeWidget = createChartWidget({
  id: 'alert-volume',
  name: 'Alert Volume',
  description: 'Alerts over time',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const volume = sourceData?.alertVolume || sourceData?.alert_history || [];
    return {
      chartType: 'bar',
      data: volume,
      xKey: 'day',
      yKey: 'alerts',
      color: AMBER,
      link: '/cloudops/guardrails',
    };
  },
});

/**
 * Cloud Spend Trend Widget
 * Shows historical spending
 */
export const cloudSpendTrendWidget = createChartWidget({
  id: 'cloud-spend-trend',
  name: 'Cloud Spend Trend',
  description: 'Historical spending',
  icon: FiDollarSign,
  category: WIDGET_CATEGORIES.PLATFORMOPS,
  defaultSize: WIDGET_SIZES.LARGE,
  dataSource: DATA_SOURCES.PLATFORMOPS,
  supportsDateFilter: true,
  refreshRate: 600000,
  getData: (sourceData) => {
    const trend = sourceData?.cloudSpendTrend || sourceData?.spend_history || [];
    return {
      chartType: 'area',
      data: trend,
      xKey: 'month',
      yKey: 'spend',
      color: CYAN,
      link: '/cloudops/finops',
    };
  },
});

/**
 * Export all PlatformOps widgets as an array
 */
export const PLATFORMOPS_WIDGETS = [
  // Original widgets
  platformHealthWidget,
  totalCloudSpendWidget,
  complianceScoreWidget,
  costByServiceWidget,
  activeEnvironmentsWidget,
  openViolationsWidget,
  estimatedSavingsWidget,
  // Additional widgets (Phase 10)
  environmentHealthWidget,
  tagComplianceWidget,
  costAnomaliesWidget,
  monthlyCostTrendWidget,
  serviceCostBreakdownAltWidget,
  riUtilizationWidget,
  spotSavingsWidget,
  costSavingsOpportunitiesWidget,
  violationsBySeverityWidget,
  violationsByCategoryWidget,
  environmentUptimeWidget,
  activeUsersCountWidget,
  pendingAccessRequestsWidget,
  burnRateVsPlannedWidget,
  enforcementModeWidget,
  complianceTrendWidget,
  totalUsersWidget,
  rolesConfiguredWidget,
  userActivityRateWidget,
  accessByRoleWidget,
  failedLoginsWidget,
  healthyEnvironmentsWidget,
  degradedEnvironmentsWidget,
  downEnvironmentsWidget,
  tagComplianceRateWidget,
  taggedResourcesWidget,
  untaggedResourcesWidget,
  tagCoverageWidget,
  costAllocationRateWidget,
  spendingAnomaliesWidget,
  rightsizingRecommendationsWidget,
  idleResourcesWidget,
  systemUptimeWidget,
  monthlyBudgetWidget,
  budgetAlertsWidget,
  alertVolumeWidget,
  cloudSpendTrendWidget,
];

/**
 * Export widgets as a map by ID for quick lookup
 */
export const PLATFORMOPS_WIDGETS_MAP = PLATFORMOPS_WIDGETS.reduce((acc: Record<string, WidgetTemplate>, widget) => {
  acc[widget.id] = widget;
  return acc;
}, {} as Record<string, WidgetTemplate>);

export default PLATFORMOPS_WIDGETS;
